import React, {useState, useEffect} from 'react';
import {Button, Form, Select, Modal, AutoComplete, Row, Col, Collapse} from 'antd';
import {CommandDto, ConditionDto, ConditionDtoTypeEnum, PrefabControllerApi, PrefabDto, SystemDto} from '../../../api';
import {EditOutlined, AppstoreAddOutlined} from "@ant-design/icons";
import {useKeycloakHeader} from "../../../config/Config";
import PrefabCommandOptionComponent from "./component/PrefabCommandOptionComponent";
import {useSystemContext} from "../../../utils/hook/SystemProvider";


function createConditionDto(values: any): ConditionDto | null {
    // Überprüfe, ob ein conditionType ausgewählt wurde
    if (!values.conditionType) {
        return null;
    }

    // Erstelle ein Basisobjekt für ConditionDto
    let condition: ConditionDto = {
        type: values.conditionType,
    };

    // Füge die spezifischen Felder basierend auf dem Typ hinzu
    switch (values.conditionType) {
        case ConditionDtoTypeEnum.Exists:
            condition.variableName = values.conditionExists;
            break;

        case ConditionDtoTypeEnum.NotExists:
            condition.variableName = values.conditionNotExists;
            break;

        case ConditionDtoTypeEnum.Equals:
            condition.variableName = values.conditionEqualsVar;
            condition.checkValue = values.conditionEqualsValue;
            break;

        case ConditionDtoTypeEnum.NotEquals:
            condition.variableName = values.conditionNotEqualsVar;
            condition.checkValue = values.conditionNotEqualsValue;
            break;

        default:
            break;
    }

    return condition;
}

const PrefabCommandModal = ({onCommandCardData, initialData}: { onCommandCardData: Function; initialData?: CommandDto }) => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [prefabs, setPrefabs] = useState<PrefabDto[]>([]);
    const [selectedPrefab, setSelectedPrefab] = useState<PrefabDto | undefined>();
    const [searchValue, setSearchValue] = useState('');
    const [selectedSystem, setSelectedSystem] = useState<SystemDto | undefined>(); // State für ausgewähltes System

    const headers = useKeycloakHeader();
    const prefabControllerApi = new PrefabControllerApi();
    const { systems, triggerRefresh } = useSystemContext();
    const {Option} = Select;

    useEffect(() => {
        // Wenn initialData vorhanden ist, fülle das Formular mit den initialen Daten
        if (initialData && initialData.prefabId) {
            fetchPrefabByIdAndSetSelectedPrefab(initialData.prefabId);
            if (initialData.condition) {
                const condition = initialData.condition;
                switch (condition.type){
                    case "VAR_EXISTS":
                        form.setFieldValue("conditionExists", condition.variableName);
                        break;
                    case "VAR_NOT_EXISTS":
                        form.setFieldValue("conditionNotExists", condition.variableName);
                        break;
                    case "VAR_EQUALS":
                        form.setFieldValue("conditionEqualsVar", condition.variableName);
                        form.setFieldValue("conditionEqualsValue", condition.checkValue);
                        break;
                    case "VAR_NOT_EQUALS":
                        form.setFieldValue("conditionNotEqualsVar", condition.variableName);
                        form.setFieldValue("conditionNotEqualsValue", condition.checkValue);
                }
                form.setFieldValue("conditionType", condition.type);
            }
        }
    }, [initialData]);


    useEffect(() => {
        if (isModalVisible) {
            fetchPrefabs();
        }
    }, [isModalVisible, selectedSystem]); // Hinzugefügt: selectedSystem als Abhängigkeit

    const fetchPrefabByIdAndSetSelectedPrefab = (id: number) => {
        prefabControllerApi.getPrefabById(id, headers).then((axiosResponse) => {
            const prefabDto: PrefabDto = axiosResponse.data;
            setSelectedPrefab(prefabDto);
            setSelectedSystem(prefabDto.system)
            form.setFieldsValue({prefabName: prefabDto.name, system: prefabDto.system.name});
        });
    }

    const fetchPrefabs = async () => {
        if (!selectedSystem || !selectedSystem.name) {
            return; // Wenn kein System ausgewählt ist, keinen fetchPrefabs-Aufruf durchführen
        }

        prefabControllerApi
            .getAllPrefabsBySystem(selectedSystem.name, headers) // Verwende das ausgewählte System
            .then((axiosResponse) => {
                const axiosData: Array<PrefabDto> = axiosResponse.data;
                if (axiosResponse.status === 200) {
                    setPrefabs(axiosData);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const onFinish = (values) => {
        let command: CommandDto = {
            commandType: 'PREFAB',
            prefabId: selectedPrefab?.id,
            prefabName: selectedPrefab?.name,
            description: selectedPrefab?.description
        };

        // Condition hinzufügen
        let condition: ConditionDto | null= createConditionDto(values);
        if (condition) {
            command = {
                ...command,
                condition: condition
            };
        }

        onCommandCardData(command);
        setIsModalVisible(false);
        form.resetFields();
    };

    const onReset = () => {
        form.resetFields();
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
    };

    const onSelect = (value, option) => {
        const selected = prefabs.find((p) => p.name === value);
        setSelectedPrefab(selected);
    };

    const handleSystemChange = (value) => {
        setSelectedSystem(systems.find((system) => system.id === value));
    };

    const options = prefabs
        .filter((prefab) =>
            prefab.name ? prefab.name.toLowerCase().includes(searchValue.toLowerCase()) : null
        )
        .map((prefab) => ({
            value: prefab.name,
            label: prefab.name,
        }));

    return (
        <>
            {initialData == null &&
                <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'stretch'}}>
                  <Button
                      type="primary"
                      onClick={showModal}
                      style={{width: '100%', height: '100%'}}
                  >
                    <AppstoreAddOutlined/> Komponente
                  </Button>
                </div>}
            {initialData != null &&
                <Button
                    type="primary"
                    style={{float: 'right'}}
                    onClick={showModal}
                >
                  <EditOutlined/>
                </Button>
            }
            <Modal
                title="Komponente hinzufügen"
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form
                    form={form}
                    name="control-hooks"
                    onFinish={onFinish}
                    style={{maxWidth: 600}}
                    layout="vertical"
                >
                    {/* Select-Element für das ausgewählte System */}
                    <Form.Item name="system" label="System">
                        <Select onChange={handleSystemChange}>
                            {systems.map(option => (
                                <Option key={option.id} value={option.id}>
                                    {option.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="prefabName" label="Komponente">
                        <AutoComplete
                            options={options}
                            onSelect={onSelect}
                            onSearch={handleSearch}
                            placeholder="Suche eine Komponente"
                        />
                    </Form.Item>
                    <PrefabCommandOptionComponent form={form}/>
                    <Form.Item style={{marginTop: 10}}>
                        <Row justify="center">
                            <Col style={{marginRight: 2.5}}>
                                <Button type="primary" htmlType="submit">
                                    {initialData ? "Speichern" : "Hinzufügen"}
                                </Button>
                            </Col>
                            <Col style={{marginLeft: 2.5}}>
                                <Button htmlType="button" onClick={onReset}>
                                    Zurücksetzen
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default PrefabCommandModal;
